import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useRulesStore } from '../../stores/rulesStore';
import type { TeacherPreference } from '../../types';

type TabValue = 'pending' | 'approved' | 'rejected';

export const TeacherPreferences: React.FC = () => {
  const { teacherPreferences, approveTeacherPreference, rejectTeacherPreference } = useRulesStore();

  const [activeTab, setActiveTab] = useState<TabValue>('pending');
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    preference: TeacherPreference | null;
    action: 'approve' | 'reject' | null;
  }>({ open: false, preference: null, action: null });
  const [reviewNotes, setReviewNotes] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleReview = (preference: TeacherPreference, action: 'approve' | 'reject') => {
    setReviewDialog({ open: true, preference, action });
    setReviewNotes(preference.notes || '');
  };

  const handleSubmitReview = () => {
    if (!reviewDialog.preference) return;

    const reviewedBy = 'admin-1'; // In a real app, this would be the current user ID

    if (reviewDialog.action === 'approve') {
      approveTeacherPreference(reviewDialog.preference.id, reviewedBy, reviewNotes);
    } else {
      rejectTeacherPreference(reviewDialog.preference.id, reviewedBy, reviewNotes);
    }

    setReviewDialog({ open: false, preference: null, action: null });
    setReviewNotes('');
  };

  const handleCloseReviewDialog = () => {
    setReviewDialog({ open: false, preference: null, action: null });
    setReviewNotes('');
  };

  const filteredPreferences = teacherPreferences.filter((pref) => pref.status === activeTab);

  const getPriorityColor = (priority: TeacherPreference['priority']) => {
    const colors: Record<TeacherPreference['priority'], 'error' | 'warning' | 'info'> = {
      high: 'error',
      medium: 'warning',
      low: 'info',
    };
    return colors[priority];
  };

  const _getStatusColor = (status: TeacherPreference['status']) => {
    const colors: Record<TeacherPreference['status'], 'warning' | 'success' | 'error'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status];
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Teacher Preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Review and manage individual teacher scheduling preferences and requests
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Pending Review
              </Typography>
              <Typography variant="h3">{teacherPreferences.filter((p) => p.status === 'pending').length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Approved
              </Typography>
              <Typography variant="h3">{teacherPreferences.filter((p) => p.status === 'approved').length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Rejected
              </Typography>
              <Typography variant="h3">{teacherPreferences.filter((p) => p.status === 'rejected').length}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Pending Review" value="pending" />
            <Tab label="Approved" value="approved" />
            <Tab label="Rejected" value="rejected" />
          </Tabs>
        </Paper>

        {/* Preferences Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Submitted</TableCell>
                {activeTab !== 'pending' && <TableCell>Reviewed</TableCell>}
                {activeTab !== 'pending' && <TableCell>Notes</TableCell>}
                {activeTab === 'pending' && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPreferences.map((pref) => (
                <TableRow key={pref.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{pref.teacherName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={pref.type.replace(/_/g, ' ')} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{pref.description}</Typography>
                    {pref.requestedValue && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Value: {pref.requestedValue}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={pref.priority} color={getPriorityColor(pref.priority)} size="small" />
                  </TableCell>
                  <TableCell>{formatDate(pref.submittedAt)}</TableCell>
                  {activeTab !== 'pending' && <TableCell>{formatDate(pref.reviewedAt)}</TableCell>}
                  {activeTab !== 'pending' && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {pref.notes || 'No notes'}
                      </Typography>
                    </TableCell>
                  )}
                  {activeTab === 'pending' && (
                    <TableCell align="right">
                      <Button
                        startIcon={<CheckIcon />}
                        onClick={() => handleReview(pref, 'approve')}
                        color="success"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        Approve
                      </Button>
                      <Button
                        startIcon={<CloseIcon />}
                        onClick={() => handleReview(pref, 'reject')}
                        color="error"
                        size="small"
                      >
                        Reject
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Review Dialog */}
        <Dialog open={reviewDialog.open} onClose={handleCloseReviewDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{reviewDialog.action === 'approve' ? 'Approve' : 'Reject'} Teacher Preference</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Teacher: {reviewDialog.preference?.teacherName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {reviewDialog.preference?.description}
              </Typography>
              <TextField
                label="Review Notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 2 }}
                helperText={reviewDialog.action === 'reject' ? 'Required for rejections' : 'Optional'}
                required={reviewDialog.action === 'reject'}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReviewDialog}>Cancel</Button>
            <Button
              onClick={handleSubmitReview}
              variant="contained"
              color={reviewDialog.action === 'approve' ? 'success' : 'error'}
              disabled={reviewDialog.action === 'reject' && !reviewNotes.trim()}
            >
              {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TeacherPreferences;
